import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import startCase from 'lodash.startcase';
import { rem } from '@gbi/ui';
import SectionLoader from '../Loader/SectionLoader';
import BasicModalBody from '../BasicModal/BasicModalBody';
import ModalButtons from '../Modal/ModalButtons';
import CheckboxGTT from '../InputFields/CheckboxGTT';
import PrintModal from '../PrintModal/PrintModal';
import ReviewInfoRow from './ReviewInfoRow';
import { ReactComponent as InfoIcon } from '../../assets/images/svg/info-ico.svg';
import { formatMoneyNumeral } from '../../util/helpers/numeralHelpers';
import { parseEnumType } from '../../util/helpers/enumMappers';
import { productLocationTypes } from '../../util/enum/api/productTypes';
import {
  getCurrentAccountUid,
  selectAccountAgreementHtml,
} from '../../store/selectors/accountSelectors';
import {
  fetchAccountAgreement,
  submitAccountAgreements,
} from '../../store/actions/account/accountActions';
import { FETCH_ACCOUNT_AGREEMENT_HTML_LOADING } from '../../store/actions/account/accountActionConstants';
import { portfolioFinancialPendingTransactions } from '../../store/actions/portfolio/portfolioActions';
import { executeTriggerOrderQuote } from '../../store/actions/triggerOrders/triggerOrdersActions';
import { EXECUTE_TRIGGER_ORDER_QUOTE_LOADING } from '../../store/actions/triggerOrders/triggerOrdersActionConstants';
import { selectSettings } from '../../store/selectors/settingsSelectors';
import { selectIsLoadingByActionTypes } from '../../store/selectors/loadingSelectors';
import { AGREEMENT_TYPES } from '../../constants/agreementConstants';
import { FINANCIAL_TRANSACTIONS } from '../../constants/accountTransactionConstants';
import AgreementDisplay from '../Agreements/AgreementDisplay';
import config from '../../config';
import tokens from '../../assets/styles/tokens';
import useGtmHook from '../../util/hooks/useGtmHook';
import {
  trackTriggerOrderReviewed,
  trackGttBuyReviewButton,
  trackGttBuyCreated,
} from './Common/gtmEventHelper';
import {
  GUTTER,
  wizardButtonCss,
  ReviewProductRow,
  ReviewThumb,
  ReviewProductInfo,
  ReviewOrderLabel,
  ReviewProductName,
  StoringInVaultText,
  ReviewVaultRow,
  SegregatedTag,
  ReviewSummary,
  ReviewSummaryItem,
  ReviewDivider,
  PriceCaption,
  VolumeDiscountBanner,
  VolumeDiscountIcon,
  VolumeDiscountText,
  ReservedFundsBanner,
  ReservedFundsText,
  ReviewFooterWrapper,
  ReviewTermsRow,
  ReviewTermsText,
  ReviewTermsLink,
} from './Common';

// The wizard's gutter at every width (BasicModalBody's own padding is
// legacy-scaled), with the frames' 10px under the header.
const ReviewBody = styled(BasicModalBody)`
  padding: ${rem(10)} ${GUTTER} ${GUTTER};
`;

const savingsValueCss = `
  ${tokens.type.label[300]}
  color: ${tokens.color.status.flag};
`;

const totalValueCss = `
  ${tokens.type.title[300]}
  color: ${tokens.color.text.primary};
`;

// Third wizard step: shows a read-only summary of the target price order set
// up in TargetPriceOrderEntry (product, vault, subtotal, savings, total) and
// requires the terms checkbox before continuing (no-op until Confirmation is
// built). Modeled on TargetPriceOrderEntry for the wizard scaffolding,
// Buy/OrderReviewPriceList for the price-summary rows, and
// Modals/MetalStream/MetalStreamReview for the terms-checkbox + disabled-
// submit pattern.
const TargetPriceOrderReview = ({
  wizardData,
  setWizardData,
  setWizardTitle,
  setGoBackHandler,
  handleBack,
  handleNext,
  isLastStep,
  isGS,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { ecommerceGtmEvent } = useGtmHook();
  const accountUid = useSelector(getCurrentAccountUid);
  const { RootUrl, SegregatedCaption, InstavaultProductCodes } = useSelector(
    selectSettings,
  );

  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [isAgreementDownloading, setIsAgreementDownloading] = useState(false);
  const [isSubmittingAgreement, setIsSubmittingAgreement] = useState(false);
  // Mirrors AlertEntry: the agreement HTML is fetched inside AgreementDisplay
  // (the terms modal's children), so treat the modal as "content loading"
  // while that fetch is in flight OR the HTML hasn't arrived yet.
  const agreementHtml = useSelector(selectAccountAgreementHtml);
  const isAgreementHtmlLoading = useSelector(
    selectIsLoadingByActionTypes([FETCH_ACCOUNT_AGREEMENT_HTML_LOADING]),
  );
  const isAgreementContentLoading = isAgreementHtmlLoading || !agreementHtml;
  const isExecutingQuote = useSelector(
    selectIsLoadingByActionTypes([EXECUTE_TRIGGER_ORDER_QUOTE_LOADING]),
  );

  const product = wizardData?.selectedProduct;
  const vault = wizardData?.selectedVault;
  const quantity = Number(wizardData?.orderValues?.QuantityAmount) || 0;
  const targetPrice = Number(wizardData?.orderValues?.CashAmount) || 0;
  // The quote fetched by TargetPriceOrderEntry's GET trigger-orders/quotes
  // call (on "Review Target Order"), stashed into wizardData. BE is
  // authoritative for these once the quote exists.
  const quote = wizardData?.quote;
  // BE no longer returns VolumeDiscount; the banner shows only when the quote
  // has a tier range with LowBound > 0.
  const hasVolumeDiscount = Number(quote?.LowBound) > 0;
  // numeral abbreviates millions as lowercase "m"; show it as "M".
  const formatBound = (value) =>
    formatMoneyNumeral(value, '$0a').replace(/m$/, 'M');
  // An open-ended top tier has no HighBound, so show "<LowBound>+" instead.
  const hasHighBound = quote?.HighBound != null;
  const volumeDiscountRange = hasHighBound
    ? `${formatBound(quote?.LowBound)}–${formatBound(quote?.HighBound)}`
    : `${formatBound(quote?.LowBound)}+`;

  // The live price captured on the Entry screen when the quote was
  // requested — reused here instead of re-fetching so "Current Price"
  // matches what the user saw there rather than drifting on a fresh fetch.
  const productPrice = Number(wizardData?.productPrice) || 0;

  const vaultName = vault
    ? startCase(parseEnumType(productLocationTypes, vault.LocationType))
    : '';
  const isSegregatedStorage = wizardData?.selectedPlan === 'premium';

  // Instavault products store in one implicit location with nothing to
  // surface — mirror TargetPriceOrderEntry/Buy's isInstaVaultProduct check
  // and hide the "Storing in {vault} Vault" line entirely.
  const productCode = product?.ProductCode || product?.Code;
  const isInstaVaultProduct =
    isGS &&
    (productCode === InstavaultProductCodes?.[0] ||
      productCode === InstavaultProductCodes?.[1]);

  const subtotal = Number(quote?.Subtotal) || 0;
  const savings = Number(quote?.TargetPriceSavings) || 0;
  const totalAtExecution = Number(quote?.TotalAtExecution) || 0;

  const imageSource = product
    ? config.algoliaSearch.imageCode
        .replace('{product_code}', product.ProductCode || product.Code)
        .replace('{base_url}', RootUrl)
    : '';

  // Set the header title and route the back chevron to the previous step.
  useEffect(() => {
    setWizardTitle(t('gtt.triggerOrders.review.title'));
    setGoBackHandler(() => handleBack);
    return () => setGoBackHandler(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // gtt_buy_review_button — the user reached this screen. The quote (and with
  // it order_value/volume_tier) is already in wizardData by the time this
  // mounts, since TargetPriceOrderEntry fetches it before advancing here.
  useEffect(() => {
    trackGttBuyReviewButton({
      ecommerceGtmEvent,
      productId: productCode,
      productName: product?.Caption,
      triggerPrice: targetPrice,
      quantity,
      orderValue: totalAtExecution,
      volumeTier: hasVolumeDiscount ? volumeDiscountRange : undefined,
      vaultLocation: vaultName,
      entryPoint: wizardData?.entryPoint,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Download / view the trigger orders terms-of-use agreement PDF. Mirrors
  // AlertEntry's handleDownloadAgreement: the saga GETs
  // /accounts/{accountUid}/agreements/pdf as a blob and we open it in a new
  // tab. AGREEMENT_TYPES[10] matches the type rendered by AgreementDisplay in
  // the terms modal below.
  const handleDownloadAgreement = () => {
    dispatch(
      fetchAccountAgreement({
        accountUid,
        agreementType: AGREEMENT_TYPES[10],
        setIsLoading: setIsAgreementDownloading,
        agreementFetchSuccess: (data) => {
          if (data) {
            const objectUrl = window.URL.createObjectURL(data);
            window.open(objectUrl, '_blank', 'noopener,noreferrer');
          }
        },
      }),
    );
  };

  // Accept the terms from within the modal — records consent in the client
  // (checks the box, closes the modal). The actual acceptance is posted at
  // submit time (see handleSubmit), same as AlertEntry's SMS consent.
  const handleAcceptAgreement = () => {
    setWizardData({ areTermsChecked: true });
    setIsTermsModalOpen(false);
  };

  const handleSubmit = () => {
    // Record the trigger orders terms acceptance server-side, then execute
    // the quote fetched on Review entry (POST trigger-orders/quotes). Gate
    // advancing to the next step on both calls succeeding.
    dispatch(
      submitAccountAgreements({
        accountUid,
        agreementType: AGREEMENT_TYPES[10],
        setIsLoading: setIsSubmittingAgreement,
        onSuccess: () => {
          dispatch(
            executeTriggerOrderQuote({
              accountUid,
              QuoteUid: quote?.QuoteUid,
              onSuccess: (executedOrder) => {
                // Kept in wizardData (persisted to session storage) so the
                // Confirmation screen's "View Order" can find this order in
                // the refreshed transaction-requests list, even after a reload.
                setWizardData({
                  triggerOrderUid: executedOrder?.TriggerOrderUid,
                });
                // This flow is hosted by the Header, so the order can be placed
                // from any page. Reload the pending transactions so a Balances
                // / Activity table already on screen shows the new trigger
                // order without a manual refresh.
                dispatch(
                  portfolioFinancialPendingTransactions({
                    accountUid,
                    content: FINANCIAL_TRANSACTIONS,
                  }),
                );
                trackTriggerOrderReviewed({
                  ecommerceGtmEvent,
                  productCode: product?.ProductCode || product?.Code,
                  targetPrice,
                  quantity,
                  vault: vaultName,
                });
                // gtt_buy_created — fired here, not from the Confirmation
                // screen: this is the moment the order is actually created
                // (executeTriggerOrderQuote succeeded), and executedOrder is
                // only available in this callback. Confirmation's mount
                // effect would re-fire on every refresh, since the wizard
                // step is persisted to session storage.
                //
                // current_price uses product.ProductPrice (the catalog
                // price), the same source gtt_buy_started_buy_page/
                // gtt_buy_sku_selected use — not wizardData.productPrice,
                // which is the tier-adjusted price this screen displays and
                // would otherwise give current_price two different meanings
                // across GTT events. Numeric fields send null rather than a
                // fabricated 0 when the underlying value is missing.
                trackGttBuyCreated({
                  ecommerceGtmEvent,
                  orderId: executedOrder?.TriggerOrderUid,
                  productId: productCode,
                  productName: product?.Caption,
                  currentPrice:
                    product?.ProductPrice != null
                      ? Number(product.ProductPrice)
                      : null,
                  triggerPrice:
                    wizardData?.orderValues?.CashAmount != null
                      ? Number(wizardData.orderValues.CashAmount)
                      : null,
                  quantity:
                    wizardData?.orderValues?.QuantityAmount != null
                      ? Number(wizardData.orderValues.QuantityAmount)
                      : null,
                  orderValue:
                    quote?.TotalAtExecution != null
                      ? Number(quote.TotalAtExecution)
                      : null,
                  volumeTier: hasVolumeDiscount ? volumeDiscountRange : null,
                  vaultLocation: vaultName,
                  entryPoint: wizardData?.entryPoint,
                });
                // Guards against advancing off the last defined step (which
                // would blank the modal), mirroring ProductSelection /
                // OrderEntry — Review is only ever a middle step in practice.
                if (!isLastStep) {
                  handleNext();
                }
              },
            }),
          );
        },
      }),
    );
  };

  return (
    <>
      <SectionLoader isLoading={isSubmittingAgreement || isExecutingQuote}>
        <ReviewBody>
          <ReviewProductRow>
            <ReviewThumb src={encodeURI(imageSource)} alt={product?.Caption} />
            <ReviewProductInfo>
              <ReviewOrderLabel>
                {t('gtt.triggerOrders.review.targetOrder')}
              </ReviewOrderLabel>
              <ReviewProductName>
                {quantity} × {product?.ShortCaption}
              </ReviewProductName>
              {!isInstaVaultProduct && (
                <ReviewVaultRow>
                  <StoringInVaultText
                    translationKey="gtt.triggerOrders.review.storingIn"
                    vaultName={vaultName}
                  />
                  {isSegregatedStorage && (
                    <SegregatedTag>
                      {SegregatedCaption || t('buyWizard.premium')}
                    </SegregatedTag>
                  )}
                </ReviewVaultRow>
              )}
            </ReviewProductInfo>
          </ReviewProductRow>

          <ReviewSummary>
            <ReviewDivider />
            <ReviewSummaryItem>
              <ReviewInfoRow
                label={t('gtt.triggerOrders.review.subtotal', { quantity })}
                value={formatMoneyNumeral(subtotal)}
              />
              <PriceCaption>
                {t('gtt.triggerOrders.review.currentPrice', {
                  price: formatMoneyNumeral(productPrice),
                })}
              </PriceCaption>
            </ReviewSummaryItem>
            <ReviewInfoRow
              label={t('gtt.triggerOrders.review.targetPriceSavings')}
              value={`-${formatMoneyNumeral(savings)}`}
              valueCss={savingsValueCss}
            />
            <ReviewDivider />
            <ReviewSummaryItem>
              <ReviewInfoRow
                label={t('gtt.triggerOrders.review.totalAtExecution')}
                value={formatMoneyNumeral(totalAtExecution)}
                valueCss={totalValueCss}
              />
              <PriceCaption>
                {t('gtt.triggerOrders.review.targetPriceCaption', {
                  price: formatMoneyNumeral(targetPrice),
                  quantity,
                })}
              </PriceCaption>
            </ReviewSummaryItem>
            <ReviewDivider />
          </ReviewSummary>

          {hasVolumeDiscount && (
            <VolumeDiscountBanner>
              <VolumeDiscountIcon />
              <VolumeDiscountText>
                {t('gtt.triggerOrders.review.volumeDiscountText', {
                  range: volumeDiscountRange,
                })}
              </VolumeDiscountText>
            </VolumeDiscountBanner>
          )}

          <ReservedFundsBanner>
            <InfoIcon
              fill={tokens.color.border.focus}
              style={{ width: 18, height: 18 }}
            />
            <ReservedFundsText>
              <b>{formatMoneyNumeral(totalAtExecution)}</b>{' '}
              {t('gtt.triggerOrders.review.reservedFundsText')}
            </ReservedFundsText>
          </ReservedFundsBanner>

          <ReviewFooterWrapper>
            <ReviewDivider fullBleed />
            <ReviewTermsRow>
              <CheckboxGTT
                checked={!!wizardData?.areTermsChecked}
                onClick={() =>
                  setWizardData({
                    areTermsChecked: !wizardData?.areTermsChecked,
                  })
                }
                name="target-price-terms"
                ariaLabel={t('gtt.triggerOrders.review.agreeText')}
                raisedCheckmark
                lightBackground
              />
              <ReviewTermsText>
                {t('gtt.triggerOrders.review.agreeText')}{' '}
                <ReviewTermsLink
                  role="link"
                  tabIndex={0}
                  data-cy="link-target-price-terms"
                  onClick={(event) => {
                    event.stopPropagation();
                    event.preventDefault();
                    setIsTermsModalOpen(true);
                  }}
                >
                  {t('gtt.triggerOrders.review.termsLink')}
                </ReviewTermsLink>
              </ReviewTermsText>
            </ReviewTermsRow>

            <ModalButtons
              showOnlyPrimary
              // Scoped to this instance via ModalButtonsContainer's existing
              // `css` passthrough — the shared ModalButtons/Button components
              // themselves aren't touched, so no other screen is affected.
              css={`
                > button {
                  ${wizardButtonCss}
                }
              `}
              primaryButtonProps={{
                label: t('gtt.triggerOrders.review.setBuyTargetPrice'),
                disabled:
                  !wizardData?.areTermsChecked ||
                  !quote?.QuoteUid ||
                  isSubmittingAgreement ||
                  isExecutingQuote,
                textTransform: 'capitalize',
                onClick: handleSubmit,
                'data-cy': 'gtt-set-buy-target-price',
              }}
            />
          </ReviewFooterWrapper>
        </ReviewBody>
      </SectionLoader>

      <PrintModal
        isOpen={isTermsModalOpen}
        onClose={() => setIsTermsModalOpen(false)}
        onCloseButton={handleAcceptAgreement}
        buttonText={t('common.accept')}
        variant="lg"
        hidePrint
        shouldScrollDown
        hideHeaderShadow
        closeIconRight
        isLoading={isAgreementContentLoading || isSubmittingAgreement}
        isDownloading={isAgreementDownloading}
        onDownload={handleDownloadAgreement}
        downloadLabel={t('gtt.labels.downloadAgreement')}
      >
        <AgreementDisplay
          type={AGREEMENT_TYPES[10]}
          signTheDoc={false}
          hideLoader
        />
      </PrintModal>
    </>
  );
};

TargetPriceOrderReview.propTypes = {
  wizardData: PropTypes.shape({
    entryPoint: PropTypes.string,
    selectedProduct: PropTypes.shape({
      ProductCode: PropTypes.string,
      Code: PropTypes.string,
      Caption: PropTypes.string,
      WeightCaption: PropTypes.string,
      ProductPrice: PropTypes.number,
    }),
    selectedVault: PropTypes.shape({
      LocationType: PropTypes.number,
      SymbolCode: PropTypes.string,
    }),
    orderValues: PropTypes.shape({
      CashAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      QuantityAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    areTermsChecked: PropTypes.bool,
    triggerOrderUid: PropTypes.string,
    selectedPlan: PropTypes.string,
    productPrice: PropTypes.number,
    quote: PropTypes.shape({
      QuoteUid: PropTypes.string,
      Subtotal: PropTypes.number,
      TargetPriceSavings: PropTypes.number,
      TotalAtExecution: PropTypes.number,
      LowBound: PropTypes.number,
      HighBound: PropTypes.number,
    }),
  }),
  setWizardData: PropTypes.func,
  setWizardTitle: PropTypes.func,
  setGoBackHandler: PropTypes.func,
  handleBack: PropTypes.func,
  handleNext: PropTypes.func,
  isLastStep: PropTypes.bool,
  isGS: PropTypes.bool,
};

export default TargetPriceOrderReview;
